import React, { useEffect, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    Chip,
    CircularProgress,
    FormControlLabel,
    Grid,
    IconButton,
    InputAdornment,
    MenuItem,
    Stack,
    Switch,
    TextField,
    Typography,
    useTheme
} from '@mui/material';
import { IconArrowLeft, IconBox, IconBriefcase, IconCamera, IconFileText, IconPlus, IconTrash, IconUpload, IconX } from '@tabler/icons';
import { useHistory, useParams } from 'react-router-dom';
import api from '../../services/api';
import { maskCurrencyInput } from '../../../utils/currency';
import CustomFieldInput from '../authentication/configuracoes/shared/CustomFieldInput';
import { isFieldVisible } from '../authentication/configuracoes/shared/fieldVisibility';
import EntityPickerInline from '../ticket/EntityPickerInline';
import { TIPO_COBRANCA_LABELS, mediaUrl } from './itemUtils';

const displayCurrency = (value) =>
    value === '' || value === null || value === undefined
        ? ''
        : Number(value).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// `id: null` = variação nova (ainda não existe no servidor). `imagensExistentes`
// só é populado ao carregar um item pra edição (fotos já salvas, cada uma
// com {id, url, alt_text}); `imagensNovas`/`descricoesNovas` são fotos
// staged localmente (ainda não enviadas). O mesmo objeto serve tanto pra
// criar quanto pra editar - unificado de propósito.
const EMPTY_VARIACAO = {
    id: null,
    nome_variacao: '',
    valor_venda: '',
    valor_custo: '',
    estoque: '',
    sku: '',
    imagensExistentes: [],
    imagensNovas: [],
    descricoesNovas: []
};

const EMPTY_FORM = {
    nome: '',
    descricao: '',
    categoria: '',
    marca: '',
    modelo: '',
    ativo: true,
    valor: '',
    tipo_cobranca: 'avulso',
    recorrencia_intervalo: '',
    recorrencia_periodo: 'meses',
    duracao_minutos: ''
};

// Seção com título + ícone num badge de gradiente - estilo próprio (não
// reaproveita o `FormSectionHeader` do resto do sistema, por pedido
// explícito do usuário de um visual mais moderno/futurista aqui).
const Section = ({ icon, title, subtitle, children, isDark }) => (
    <Box
        sx={{
            borderRadius: '24px',
            p: { xs: 2.5, md: 3.5 },
            mb: 3,
            bgcolor: isDark ? 'rgba(255,255,255,0.03)' : '#fff',
            border: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : 'rgba(15,23,42,0.06)'}`
        }}
    >
        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: subtitle ? 0.5 : 2.5 }}>
            <Box
                sx={{
                    width: 40,
                    height: 40,
                    borderRadius: '14px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: 'linear-gradient(135deg,#2563eb,#7c3aed)',
                    color: '#fff',
                    flexShrink: 0
                }}
            >
                {icon}
            </Box>
            <Typography variant="h5" fontWeight={800}>
                {title}
            </Typography>
        </Stack>
        {subtitle && (
            <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2.5 }}>
                {subtitle}
            </Typography>
        )}
        {children}
    </Box>
);

const ItemFormPage = () => {
    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark';
    const history = useHistory();
    const { id } = useParams();
    const isEdit = Boolean(id);

    const [appId, setAppId] = useState(null);
    const [loading, setLoading] = useState(isEdit);
    const [saving, setSaving] = useState(false);
    const [status, setStatus] = useState({ type: '', message: '' });

    const [tipo, setTipo] = useState(null);
    const [form, setForm] = useState(EMPTY_FORM);
    const [capa, setCapa] = useState(null);
    const [capaPreview, setCapaPreview] = useState(null);
    const [profissionalResponsavel, setProfissionalResponsavel] = useState(null);
    const [variacoes, setVariacoes] = useState([]);

    const [fieldsConfig, setFieldsConfig] = useState([]);
    const [fields, setFields] = useState({});

    const [anexos, setAnexos] = useState([]);
    const [uploadingAnexo, setUploadingAnexo] = useState(false);

    useEffect(() => {
        api.get('v1/apps/default/')
            .then((response) => setAppId(response.data.app.id))
            .catch((error) => console.error(error));
    }, []);

    useEffect(() => {
        if (!appId || !isEdit) return;
        (async () => {
            try {
                const response = await api.get(`v1/apps/${appId}/catalog_items/${id}/`);
                const item = response.data;
                setTipo(item.tipo);
                setForm({
                    nome: item.nome || '',
                    descricao: item.descricao || '',
                    categoria: item.categoria || '',
                    marca: item.marca || '',
                    modelo: item.modelo || '',
                    ativo: item.ativo,
                    valor: item.preco || '',
                    tipo_cobranca: item.servico_detalhe?.tipo_cobranca || 'avulso',
                    recorrencia_intervalo: item.servico_detalhe?.recorrencia_intervalo || '',
                    recorrencia_periodo: item.servico_detalhe?.recorrencia_periodo || 'meses',
                    duracao_minutos: item.servico_detalhe?.duracao_minutos || ''
                });
                setProfissionalResponsavel(item.servico_detalhe?.profissional_responsavel_detail || null);
                if (item.imagem) setCapaPreview(mediaUrl(appId, item.imagem));
                if (item.tipo === 'produto') {
                    setVariacoes(
                        (item.variacoes || []).map((v) => ({
                            id: v.id,
                            nome_variacao: v.nome_variacao,
                            estoque: v.estoque,
                            valor_venda: v.valor_venda,
                            valor_custo: v.valor_custo,
                            sku: v.sku,
                            imagensExistentes: (v.imagens || []).map((img) => ({ id: img.id, url: img.imagem, alt_text: img.alt_text })),
                            imagensNovas: [],
                            descricoesNovas: []
                        }))
                    );
                }
                const values = {};
                (item.field_values || []).forEach((fv) => {
                    values[fv.field_name] = fv.value;
                });
                setFields(values);
            } catch (error) {
                console.error('Erro ao carregar item', error);
                setStatus({ type: 'error', message: 'Erro ao carregar item.' });
            } finally {
                setLoading(false);
            }
        })();
    }, [appId, isEdit, id]);

    useEffect(() => {
        if (!appId || !tipo) return;
        api.get(`v1/apps/${appId}/item_fields/?tipo=${tipo}`)
            .then((response) => setFieldsConfig(response.data || []))
            .catch((error) => console.error('Erro ao carregar campos customizados', error));
    }, [appId, tipo]);

    const loadAnexos = async () => {
        if (!appId || !isEdit) return;
        try {
            const response = await api.get(`v1/apps/${appId}/documents/?item=${id}`);
            setAnexos(response.data?.documents || []);
        } catch (error) {
            console.error('Erro ao carregar anexos', error);
        }
    };

    useEffect(() => {
        if (tipo === 'servico') loadAnexos();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [appId, tipo, isEdit]);

    const handleChange = (field) => (e) => setForm((prev) => ({ ...prev, [field]: e.target.value }));

    const handleCapaChange = (file) => {
        setCapa(file);
        setCapaPreview(file ? URL.createObjectURL(file) : null);
    };

    const addVariacao = () => setVariacoes([...variacoes, { ...EMPTY_VARIACAO }]);
    const updateVariacao = (i, field, value) => {
        const copy = [...variacoes];
        copy[i][field] = value;
        setVariacoes(copy);
    };
    const removeVariacao = (i) => {
        const copy = [...variacoes];
        copy.splice(i, 1);
        setVariacoes(copy);
    };
    const totalImagens = (v) => v.imagensExistentes.length + v.imagensNovas.length;
    const handleImageUpload = (i, files) => {
        const copy = [...variacoes];
        const espaco = 20 - totalImagens(copy[i]);
        const novas = Array.from(files).slice(0, espaco);
        copy[i].imagensNovas = [...copy[i].imagensNovas, ...novas];
        copy[i].descricoesNovas = [...copy[i].descricoesNovas, ...novas.map(() => '')];
        setVariacoes(copy);
    };
    const updateImagemNovaDescricao = (i, idx, value) => {
        const copy = [...variacoes];
        copy[i].descricoesNovas[idx] = value;
        setVariacoes(copy);
    };
    const removeImagemNova = (i, idx) => {
        const copy = [...variacoes];
        copy[i].imagensNovas.splice(idx, 1);
        copy[i].descricoesNovas.splice(idx, 1);
        setVariacoes(copy);
    };
    const updateImagemExistenteDescricao = (i, imgId, value) => {
        const copy = [...variacoes];
        copy[i].imagensExistentes = copy[i].imagensExistentes.map((img) => (img.id === imgId ? { ...img, alt_text: value } : img));
        setVariacoes(copy);
    };
    const removeImagemExistente = (i, imgId) => {
        const copy = [...variacoes];
        copy[i].imagensExistentes = copy[i].imagensExistentes.filter((img) => img.id !== imgId);
        setVariacoes(copy);
    };

    const handleUploadAnexo = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setUploadingAnexo(true);
        try {
            const formData = new FormData();
            formData.append('file', file);
            formData.append('item', id);
            await api.post(`v1/apps/${appId}/documents/`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
            await loadAnexos();
        } catch (error) {
            console.error('Erro ao enviar anexo', error);
            setStatus({ type: 'error', message: 'Erro ao enviar anexo.' });
        } finally {
            setUploadingAnexo(false);
            e.target.value = '';
        }
    };

    const removeAnexo = async (documentId) => {
        try {
            await api.delete(`v1/apps/${appId}/documents/${documentId}/`);
            setAnexos((prev) => prev.filter((d) => d.id !== documentId));
        } catch (error) {
            console.error('Erro ao remover anexo', error);
        }
    };

    const handleSubmit = async () => {
        if (!form.nome.trim()) {
            setStatus({ type: 'error', message: 'Informe o nome.' });
            return;
        }
        setSaving(true);
        setStatus({ type: '', message: '' });
        try {
            const visibleNames = new Set(fieldsConfig.filter((f) => isFieldVisible(f, fieldsConfig, fields)).map((f) => f.name));
            const fieldsToSend = Object.fromEntries(Object.entries(fields).filter(([name]) => visibleNames.has(name)));

            const formData = new FormData();
            formData.append('tipo', tipo);
            formData.append('fields', JSON.stringify(fieldsToSend));
            if (capa) formData.append('capa', capa);

            if (tipo === 'produto') {
                if (isEdit) {
                    // PATCH espera campos soltos (mesmo formato de sempre),
                    // não embrulhados em `item` - esse embrulho só existe
                    // pro POST de criação.
                    formData.append('nome', form.nome);
                    formData.append('descricao', form.descricao || '');
                    formData.append('marca', form.marca || '');
                    formData.append('modelo', form.modelo || '');
                    formData.append('categoria', form.categoria || '');
                    formData.append('ativo', form.ativo);
                } else {
                    formData.append(
                        'item',
                        JSON.stringify({
                            nome: form.nome,
                            descricao: form.descricao,
                            marca: form.marca,
                            modelo: form.modelo,
                            categoria: form.categoria,
                            ativo: form.ativo
                        })
                    );
                }

                // Mesmo contrato de `variacoes` pra criar e editar: cada
                // variação leva `id` (se já existir), `imagens_existentes`
                // (o que manter, com descrição possivelmente editada -
                // qualquer foto já salva que não aparecer aqui é apagada) e
                // `descricoes_novas` (paralelo aos arquivos `imagens_{i}`).
                formData.append(
                    'variacoes',
                    JSON.stringify(
                        variacoes.map((v) => ({
                            id: v.id || undefined,
                            nome_variacao: v.nome_variacao,
                            estoque: v.estoque,
                            valor_venda: v.valor_venda,
                            valor_custo: v.valor_custo,
                            sku: v.sku,
                            imagens_existentes: v.imagensExistentes.map((img) => ({ id: img.id, alt_text: img.alt_text })),
                            descricoes_novas: v.descricoesNovas
                        }))
                    )
                );
                variacoes.forEach((v, i) => {
                    v.imagensNovas.forEach((img) => formData.append(`imagens_${i}`, img));
                });
            } else {
                formData.append('nome', form.nome);
                formData.append('descricao', form.descricao || '');
                formData.append('categoria', form.categoria || '');
                formData.append('ativo', form.ativo);
                // `preco`, não `valor` - é o nome que o PATCH espera (o POST
                // de criação aceita os dois, mas o PATCH só olha `preco`).
                formData.append('preco', form.valor || 0);
                formData.append('tipo_cobranca', form.tipo_cobranca);
                if (form.tipo_cobranca === 'recorrente') {
                    formData.append('recorrencia_intervalo', form.recorrencia_intervalo || '');
                    formData.append('recorrencia_periodo', form.recorrencia_periodo);
                }
                formData.append('duracao_minutos', form.duracao_minutos || '');
                if (profissionalResponsavel?.id) formData.append('profissional_responsavel', profissionalResponsavel.id);
            }

            let response;
            if (isEdit) {
                response = await api.patch(`v1/apps/${appId}/catalog_items/${id}/`, formData, {
                    headers: { 'Content-Type': 'multipart/form-data' }
                });
            } else {
                response = await api.post(`v1/apps/${appId}/catalog_items/`, formData, {
                    headers: { 'Content-Type': 'multipart/form-data' }
                });
            }

            history.push(`/dashboard/config/catalog_edit/${response.data.id}`);
        } catch (error) {
            console.error('Erro ao salvar item', error);
            setStatus({ type: 'error', message: error.response?.data?.error || 'Erro ao salvar.' });
        } finally {
            setSaving(false);
        }
    };

    const cardBorder = isDark ? 'rgba(255,255,255,0.08)' : 'rgba(15,23,42,0.06)';

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
                <CircularProgress />
            </Box>
        );
    }

    // -- Passo 1: escolher o tipo (só na criação) -------------------------
    if (!isEdit && !tipo) {
        return (
            <Box sx={{ p: { xs: 2, md: 4 }, maxWidth: 900, mx: 'auto' }}>
                <Button
                    startIcon={<IconArrowLeft size={18} />}
                    onClick={() => history.push('/dashboard/config/catalog')}
                    sx={{ textTransform: 'none', mb: 3 }}
                >
                    Voltar pro catálogo
                </Button>
                <Typography sx={{ fontWeight: 900, fontSize: '2rem', mb: 1 }}>O que você quer cadastrar?</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
                    Escolha um tipo pra continuar.
                </Typography>
                <Grid container spacing={3}>
                    {[
                        {
                            tipo: 'produto',
                            label: 'Produto',
                            desc: 'Algo físico, com estoque e variações (cor, tamanho...)',
                            icon: <IconBox size={36} />
                        },
                        {
                            tipo: 'servico',
                            label: 'Serviço',
                            desc: 'Uma prestação, avulsa ou recorrente, com ou sem agenda',
                            icon: <IconBriefcase size={36} />
                        }
                    ].map((opt) => (
                        <Grid item xs={12} sm={6} key={opt.tipo}>
                            <Box
                                onClick={() => setTipo(opt.tipo)}
                                sx={{
                                    cursor: 'pointer',
                                    borderRadius: '28px',
                                    p: 4,
                                    height: 220,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    justifyContent: 'center',
                                    background: isDark
                                        ? 'linear-gradient(135deg, rgba(37,99,235,0.18), rgba(124,58,237,0.18))'
                                        : 'linear-gradient(135deg, #eef2ff, #f5f3ff)',
                                    border: `1px solid ${cardBorder}`,
                                    transition: 'transform .18s ease, box-shadow .18s ease',
                                    '&:hover': {
                                        transform: 'translateY(-4px)',
                                        boxShadow: isDark ? '0 16px 32px rgba(0,0,0,0.5)' : '0 16px 32px rgba(15,23,42,0.15)'
                                    }
                                }}
                            >
                                <Box sx={{ color: isDark ? '#c7d2fe' : '#4338ca', mb: 2 }}>{opt.icon}</Box>
                                <Typography variant="h4" fontWeight={800}>
                                    {opt.label}
                                </Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                    {opt.desc}
                                </Typography>
                            </Box>
                        </Grid>
                    ))}
                </Grid>
            </Box>
        );
    }

    // -- Passo 2: formulário -------------------------------------------
    return (
        <Box sx={{ p: { xs: 2, md: 4 }, maxWidth: 900, mx: 'auto' }}>
            <Button
                startIcon={<IconArrowLeft size={18} />}
                onClick={() => (isEdit ? history.push('/dashboard/config/catalog') : setTipo(null))}
                sx={{ textTransform: 'none', mb: 2, color: 'text.secondary' }}
            >
                Voltar
            </Button>

            <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 3 }}>
                <Chip
                    icon={tipo === 'produto' ? <IconBox size={14} /> : <IconBriefcase size={14} />}
                    label={tipo === 'produto' ? 'Produto' : 'Serviço'}
                    sx={{ fontWeight: 700 }}
                />
                <Typography sx={{ fontWeight: 900, fontSize: { xs: '1.5rem', md: '2rem' } }}>
                    {isEdit ? `Editar ${form.nome || ''}` : `Novo ${tipo === 'produto' ? 'Produto' : 'Serviço'}`}
                </Typography>
            </Stack>

            {status.message && (
                <Alert severity={status.type} sx={{ mb: 2, borderRadius: '16px' }} onClose={() => setStatus({ type: '', message: '' })}>
                    {status.message}
                </Alert>
            )}

            {/* CAPA + INFO BÁSICA */}
            <Section icon={<IconCamera size={20} />} title="Capa e informações" isDark={isDark}>
                <Grid container spacing={2.5}>
                    <Grid item xs={12} sm={4}>
                        <Box
                            component="label"
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                flexDirection: 'column',
                                width: '100%',
                                aspectRatio: '1 / 1',
                                borderRadius: '20px',
                                border: `1.5px dashed ${cardBorder}`,
                                cursor: 'pointer',
                                overflow: 'hidden',
                                position: 'relative',
                                bgcolor: isDark ? 'rgba(255,255,255,0.02)' : '#fafafa'
                            }}
                        >
                            {capaPreview ? (
                                <Box component="img" src={capaPreview} sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                            ) : (
                                <>
                                    <IconUpload size={28} color={isDark ? '#94a3b8' : '#64748b'} />
                                    <Typography variant="caption" sx={{ mt: 1, color: 'text.secondary' }}>
                                        Foto de capa
                                    </Typography>
                                </>
                            )}
                            <input type="file" hidden accept="image/*" onChange={(e) => handleCapaChange(e.target.files?.[0] || null)} />
                        </Box>
                    </Grid>
                    <Grid item xs={12} sm={8}>
                        <Stack spacing={2}>
                            <TextField label="Nome" value={form.nome} onChange={handleChange('nome')} fullWidth required autoFocus />
                            <TextField
                                label="Descrição"
                                value={form.descricao}
                                onChange={handleChange('descricao')}
                                fullWidth
                                multiline
                                rows={2}
                            />
                            <Box display="flex" gap={2}>
                                <TextField label="Categoria" value={form.categoria} onChange={handleChange('categoria')} fullWidth />
                                <FormControlLabel
                                    control={
                                        <Switch checked={form.ativo} onChange={(e) => setForm({ ...form, ativo: e.target.checked })} />
                                    }
                                    label="Ativo"
                                    sx={{ whiteSpace: 'nowrap' }}
                                />
                            </Box>
                        </Stack>
                    </Grid>
                </Grid>
            </Section>

            {tipo === 'produto' ? (
                <>
                    <Section icon={<IconBox size={20} />} title="Detalhes do produto" isDark={isDark}>
                        <Box display="flex" gap={2}>
                            <TextField label="Marca" value={form.marca} onChange={handleChange('marca')} fullWidth />
                            <TextField label="Modelo" value={form.modelo} onChange={handleChange('modelo')} fullWidth />
                        </Box>
                    </Section>

                    <Section
                        icon={<IconPlus size={20} />}
                        title="Variações"
                        subtitle="Cada variação tem seu próprio preço, estoque e até 20 fotos (com descrição)"
                        isDark={isDark}
                    >
                        <Stack spacing={2}>
                            {variacoes.map((v, i) => (
                                <Box
                                    key={v.id || `nova-${i}`}
                                    sx={{
                                        p: 2.5,
                                        borderRadius: '18px',
                                        border: `1px solid ${cardBorder}`,
                                        bgcolor: isDark ? 'rgba(255,255,255,0.02)' : '#fafafa'
                                    }}
                                >
                                    <Grid container spacing={1.5}>
                                        <Grid item xs={12}>
                                            <TextField
                                                label="Nome da variação"
                                                fullWidth
                                                size="small"
                                                placeholder="Ex: Azul - G"
                                                value={v.nome_variacao}
                                                onChange={(e) => updateVariacao(i, 'nome_variacao', e.target.value)}
                                            />
                                        </Grid>
                                        <Grid item xs={6} sm={3}>
                                            <TextField
                                                label="Custo"
                                                size="small"
                                                fullWidth
                                                value={displayCurrency(v.valor_custo)}
                                                onChange={(e) =>
                                                    updateVariacao(i, 'valor_custo', maskCurrencyInput(e.target.value).numeric)
                                                }
                                                InputProps={{ startAdornment: <InputAdornment position="start">R$</InputAdornment> }}
                                            />
                                        </Grid>
                                        <Grid item xs={6} sm={3}>
                                            <TextField
                                                label="Venda"
                                                size="small"
                                                fullWidth
                                                value={displayCurrency(v.valor_venda)}
                                                onChange={(e) =>
                                                    updateVariacao(i, 'valor_venda', maskCurrencyInput(e.target.value).numeric)
                                                }
                                                InputProps={{ startAdornment: <InputAdornment position="start">R$</InputAdornment> }}
                                            />
                                        </Grid>
                                        <Grid item xs={6} sm={3}>
                                            <TextField
                                                label="Estoque"
                                                size="small"
                                                fullWidth
                                                value={v.estoque}
                                                onChange={(e) => updateVariacao(i, 'estoque', e.target.value.replace(/\D/g, ''))}
                                            />
                                        </Grid>
                                        <Grid item xs={6} sm={3}>
                                            <TextField
                                                label="SKU"
                                                size="small"
                                                fullWidth
                                                value={v.sku}
                                                onChange={(e) => updateVariacao(i, 'sku', e.target.value)}
                                            />
                                        </Grid>

                                        <Grid item xs={12}>
                                            <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary' }}>
                                                Fotos ({totalImagens(v)}/20)
                                            </Typography>
                                            <Stack spacing={1} sx={{ mt: 1 }}>
                                                {v.imagensExistentes.map((img) => (
                                                    <Stack key={img.id} direction="row" spacing={1.5} alignItems="center">
                                                        <Box
                                                            sx={{
                                                                width: 48,
                                                                height: 48,
                                                                borderRadius: '10px',
                                                                overflow: 'hidden',
                                                                flexShrink: 0
                                                            }}
                                                        >
                                                            <Box
                                                                component="img"
                                                                src={mediaUrl(appId, img.url)}
                                                                sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                                            />
                                                        </Box>
                                                        <TextField
                                                            size="small"
                                                            fullWidth
                                                            placeholder="Descrição da foto (opcional)"
                                                            value={img.alt_text || ''}
                                                            onChange={(e) => updateImagemExistenteDescricao(i, img.id, e.target.value)}
                                                        />
                                                        <IconButton
                                                            size="small"
                                                            color="error"
                                                            onClick={() => removeImagemExistente(i, img.id)}
                                                        >
                                                            <IconTrash size={16} />
                                                        </IconButton>
                                                    </Stack>
                                                ))}
                                                {v.imagensNovas.map((img, idx) => (
                                                    <Stack key={idx} direction="row" spacing={1.5} alignItems="center">
                                                        <Box
                                                            sx={{
                                                                width: 48,
                                                                height: 48,
                                                                borderRadius: '10px',
                                                                overflow: 'hidden',
                                                                flexShrink: 0
                                                            }}
                                                        >
                                                            <Box
                                                                component="img"
                                                                src={URL.createObjectURL(img)}
                                                                sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                                            />
                                                        </Box>
                                                        <TextField
                                                            size="small"
                                                            fullWidth
                                                            placeholder="Descrição da foto (opcional)"
                                                            value={v.descricoesNovas[idx] || ''}
                                                            onChange={(e) => updateImagemNovaDescricao(i, idx, e.target.value)}
                                                        />
                                                        <IconButton size="small" color="error" onClick={() => removeImagemNova(i, idx)}>
                                                            <IconTrash size={16} />
                                                        </IconButton>
                                                    </Stack>
                                                ))}
                                                {totalImagens(v) < 20 && (
                                                    <Button
                                                        component="label"
                                                        size="small"
                                                        startIcon={<IconUpload size={16} />}
                                                        sx={{ textTransform: 'none', alignSelf: 'flex-start' }}
                                                    >
                                                        Adicionar fotos
                                                        <input
                                                            type="file"
                                                            hidden
                                                            multiple
                                                            accept="image/*"
                                                            onChange={(e) => handleImageUpload(i, e.target.files)}
                                                        />
                                                    </Button>
                                                )}
                                            </Stack>
                                        </Grid>

                                        <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                                            <Button
                                                size="small"
                                                color="error"
                                                startIcon={<IconTrash size={14} />}
                                                onClick={() => removeVariacao(i)}
                                                sx={{ textTransform: 'none' }}
                                            >
                                                Remover variação
                                            </Button>
                                        </Grid>
                                    </Grid>
                                </Box>
                            ))}
                            <Button
                                startIcon={<IconPlus size={16} />}
                                onClick={addVariacao}
                                sx={{ textTransform: 'none', fontWeight: 700, alignSelf: 'flex-start' }}
                            >
                                Adicionar variação
                            </Button>
                        </Stack>
                    </Section>
                </>
            ) : (
                <>
                    <Section icon={<IconBriefcase size={20} />} title="Cobrança" isDark={isDark}>
                        <Stack spacing={2}>
                            <Box display="flex" gap={2}>
                                <TextField
                                    select
                                    label="Tipo de cobrança"
                                    value={form.tipo_cobranca}
                                    onChange={handleChange('tipo_cobranca')}
                                    fullWidth
                                >
                                    {Object.entries(TIPO_COBRANCA_LABELS).map(([value, label]) => (
                                        <MenuItem key={value} value={value}>
                                            {label}
                                        </MenuItem>
                                    ))}
                                </TextField>
                                <TextField
                                    label="Valor"
                                    value={displayCurrency(form.valor)}
                                    onChange={(e) => setForm({ ...form, valor: maskCurrencyInput(e.target.value).numeric })}
                                    fullWidth
                                    InputProps={{ startAdornment: <InputAdornment position="start">R$</InputAdornment> }}
                                />
                            </Box>

                            {form.tipo_cobranca === 'recorrente' && (
                                <Box display="flex" gap={2}>
                                    <TextField
                                        label="A cada"
                                        type="number"
                                        value={form.recorrencia_intervalo}
                                        onChange={handleChange('recorrencia_intervalo')}
                                        fullWidth
                                    />
                                    <TextField
                                        select
                                        label="Período"
                                        value={form.recorrencia_periodo}
                                        onChange={handleChange('recorrencia_periodo')}
                                        fullWidth
                                    >
                                        <MenuItem value="dias">Dias</MenuItem>
                                        <MenuItem value="semanas">Semanas</MenuItem>
                                        <MenuItem value="meses">Meses</MenuItem>
                                    </TextField>
                                </Box>
                            )}

                            <Box display="flex" gap={2}>
                                <TextField
                                    label="Duração (minutos, opcional)"
                                    type="number"
                                    value={form.duracao_minutos}
                                    onChange={handleChange('duracao_minutos')}
                                    fullWidth
                                />
                            </Box>

                            <EntityPickerInline
                                label="Profissional responsável (opcional)"
                                placeholder="Buscar por nome ou e-mail..."
                                searchUrl={(term) => `users/?search=${term}`}
                                extractResults={(data) => data}
                                getOptionLabel={(u) => `${u.first_name || ''} ${u.last_name || ''} (${u.email})`}
                                value={profissionalResponsavel}
                                onSelect={setProfissionalResponsavel}
                                onClear={() => setProfissionalResponsavel(null)}
                                clearLabel="Sem responsável"
                            />
                        </Stack>
                    </Section>

                    {isEdit ? (
                        <Section icon={<IconFileText size={20} />} title="Anexos" subtitle="Fotos ou PDF do serviço" isDark={isDark}>
                            <Stack spacing={1.5}>
                                {anexos.map((doc) => (
                                    <Stack
                                        key={doc.id}
                                        direction="row"
                                        alignItems="center"
                                        spacing={1.5}
                                        sx={{ p: 1.2, borderRadius: '14px', bgcolor: isDark ? 'rgba(255,255,255,0.03)' : '#fafafa' }}
                                    >
                                        {doc.mime_type?.includes('pdf') ? <IconFileText size={20} /> : <IconCamera size={20} />}
                                        <Typography
                                            component="a"
                                            href={mediaUrl(appId, doc.r2_key)}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            variant="body2"
                                            sx={{
                                                flex: 1,
                                                color: 'inherit',
                                                textDecoration: 'none',
                                                '&:hover': { textDecoration: 'underline' }
                                            }}
                                        >
                                            {doc.name}
                                        </Typography>
                                        <IconButton size="small" color="error" onClick={() => removeAnexo(doc.id)}>
                                            <IconX size={16} />
                                        </IconButton>
                                    </Stack>
                                ))}
                                <Button
                                    component="label"
                                    startIcon={<IconUpload size={16} />}
                                    disabled={uploadingAnexo}
                                    sx={{ textTransform: 'none', fontWeight: 700, alignSelf: 'flex-start' }}
                                >
                                    {uploadingAnexo ? <CircularProgress size={16} /> : 'Adicionar arquivo'}
                                    <input type="file" hidden accept="image/*,application/pdf" onChange={handleUploadAnexo} />
                                </Button>
                            </Stack>
                        </Section>
                    ) : (
                        <Alert severity="info" sx={{ mb: 3, borderRadius: '16px' }}>
                            Salve o serviço primeiro pra poder anexar fotos ou PDF.
                        </Alert>
                    )}
                </>
            )}

            {fieldsConfig.length > 0 && (
                <Section icon={<IconPlus size={20} />} title="Campos personalizados" isDark={isDark}>
                    <Grid container spacing={2}>
                        {fieldsConfig
                            .filter((f) => isFieldVisible(f, fieldsConfig, fields))
                            .map((f) => (
                                <CustomFieldInput
                                    key={f.id}
                                    field={f}
                                    value={fields[f.name]}
                                    onChange={(value) => setFields({ ...fields, [f.name]: value })}
                                />
                            ))}
                    </Grid>
                </Section>
            )}

            <Stack direction="row" spacing={2} sx={{ mb: 6 }}>
                <Button
                    variant="contained"
                    size="large"
                    onClick={handleSubmit}
                    disabled={saving}
                    sx={{
                        borderRadius: '16px',
                        px: 4,
                        textTransform: 'none',
                        fontWeight: 800,
                        background: 'linear-gradient(135deg,#2563eb,#7c3aed)',
                        boxShadow: '0 8px 24px rgba(124,58,237,0.35)'
                    }}
                >
                    {saving ? (
                        <CircularProgress size={22} sx={{ color: '#fff' }} />
                    ) : isEdit ? (
                        'Salvar alterações'
                    ) : (
                        `Criar ${tipo === 'produto' ? 'produto' : 'serviço'}`
                    )}
                </Button>
                <Button
                    variant="outlined"
                    size="large"
                    onClick={() => history.push('/dashboard/config/catalog')}
                    sx={{ borderRadius: '16px', textTransform: 'none' }}
                >
                    Cancelar
                </Button>
            </Stack>
        </Box>
    );
};

export default ItemFormPage;
